import { createAuthClient } from "better-auth/react";
import { env } from "@/app/config/env";
import { secureStore } from "@/app/platform";
import { reportError } from "@/app/observability";

/**
 * SESSIONS ON A PHONE ARE BEARER TOKENS, NOT COOKIES.
 *
 * The web view serves the app from `capacitor://localhost` while the service lives on
 * https — a cross-site pair, where a cookie needs `SameSite=None; Secure` and is then
 * subject to WebKit's tracking-prevention rules and to being dropped on app relaunch.
 * People discover this as "it logs me out sometimes", which is unfixable from the app.
 *
 * So the service runs the auth library's bearer mode: it returns the session token in a
 * `set-auth-token` header, this file stores it in the Keychain, and every request carries
 * it in `Authorization`. The web build takes the same path, which keeps ONE auth story
 * instead of two — and localStorage is the honest equivalent there.
 */
const TOKEN_KEY = "auth.session-token";

let cachedToken: string | null = null;

/** Read once at start-up, before the first request needs it. */
export async function restoreAuthToken(): Promise<string | null> {
  cachedToken = await secureStore.get(TOKEN_KEY);
  return cachedToken;
}

export function getAuthToken(): string | null {
  return cachedToken;
}

/**
 * Adopts a token that arrived some way other than a fetch response — today that is the
 * deep link a social provider hands back (see src/app/lib/deep-links.ts).
 */
export async function adoptAuthToken(token: string): Promise<void> {
  await storeAuthToken(token);
}

/**
 * THE WRITE THAT NOBODY WAS WATCHING.
 *
 * This is called from `onSuccess` below as a floating promise, so a rejection here used to
 * become an unhandled rejection and nothing else: the session worked perfectly until the
 * app was closed, and was gone on the next launch with no error anywhere to connect the two.
 * That is the single most expensive silent failure on a phone, so it is reported.
 */
async function storeAuthToken(token: string): Promise<void> {
  cachedToken = token;
  try {
    await secureStore.set(TOKEN_KEY, token);
  } catch (error) {
    reportError(error, { operation: "auth.storeToken" });
  }
}

/**
 * SIGNING OUT MUST NEVER BE BLOCKED BY LOCAL CLEAN-UP.
 *
 * The in-memory token is dropped first and unconditionally, so the app is signed out the
 * moment this is called. Deleting the stored copy can fail — a keychain can refuse a write
 * on a device for reasons the app cannot influence — and letting that rejection escape made
 * `signOut()` throw AFTER the service had already revoked the session. The mutation then
 * never reached its success path, so the cached user was never cleared and the screen sat
 * there looking signed in. Pressing the button again just revoked an already-revoked
 * session. From the outside: "sign out does nothing."
 *
 * A stored token we failed to delete is not dangerous — it is already revoked server-side,
 * so the next launch gets a 401 and signs out. Silence about it would be, which is why it
 * is reported rather than swallowed.
 */
export async function clearAuthToken(): Promise<void> {
  cachedToken = null;
  try {
    await secureStore.remove(TOKEN_KEY);
  } catch (error) {
    reportError(error, { operation: "auth.clearToken" });
  }
}

export const authClient = createAuthClient({
  baseURL: `${env.apiUrl}/v1/auth`,
  fetchOptions: {
    auth: { type: "Bearer", token: () => cachedToken ?? "" },
    onSuccess(ctx) {
      // Issued on sign-in AND rotated on refresh, so this is the only place that writes it.
      const token = ctx.response.headers.get("set-auth-token");
      if (token) void storeAuthToken(token);
    },
  },
});

export type AuthClient = typeof authClient;
